import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Bot,
  Clock,
  MessageSquare,
  Image as ImageIcon,
  AlertCircle,
  Calendar,
  Database
} from 'lucide-react';
import { adminService } from '../services/AdminService';
import { useAuth } from '../contexts/AuthContext';
import JsonTreeViewer from '../components/common/JsonTreeViewer';
import PartDecisionsPanel from './PartDecisionsPanel';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';

interface SessionUser {
  uid: string;
  name: string;
  email: string;
  picture?: string;
}

interface SessionDetails {
  id: string;
  session_id: string;
  user_id: string;
  app_name: string;
  created_at: string;
  updated_at: string;
  state: any;
}

interface SessionEvent {
  id?: string;
  invocation_id: string;
  author: string;
  content?: {
    role: string;
    parts: Array<{
      text?: string;
      function_call?: any;
      function_response?: any;
    }>;
  };
  actions?: {
    state_delta?: any;
  };
  timestamp: string;
  // ADK 2.0: new graph-state tracking fields
  node_info?: any;
  output?: any;
}

interface SessionDetailsResponse {
  status: string;
  user: SessionUser;
  session: SessionDetails;
  events: SessionEvent[];
}

const AdminSessionDetailsPage: React.FC = () => {
  const { userId, sessionId } = useParams<{ userId: string; sessionId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [sessionData, setSessionData] = useState<SessionDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSystemEvents, setShowSystemEvents] = useState(false);
  const [eventFilter, setEventFilter] = useState<'all' | 'conversation' | 'system'>('conversation');

  useEffect(() => {
    if (userId && sessionId) {
      fetchSessionDetails();
    }
  }, [userId, sessionId]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userId || !sessionId) {
        setError('Missing session ID or user ID');
        setLoading(false);
        return;
      }

      const response = await adminService.getSessionDetails(userId, sessionId);
      const data = response.data;

      // Ensure events is always an array
      if (data && !data.events) {
        data.events = [];
      }

      setSessionData(data);
    } catch (err: any) {
      console.error('Failed to fetch session details:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.response?.status === 401) {
        setError('Authentication required. Please log in again.');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('Session not found.');
      } else {
        setError('Failed to load session details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      // Handle various timestamp formats
      let date: Date;

      // If it's a Unix timestamp (number as string)
      if (/^\d+$/.test(dateString)) {
        const timestamp = parseInt(dateString);
        // Check if it's in seconds (10 digits) or milliseconds (13 digits)
        date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
      } else {
        // Regular date string
        date = new Date(dateString);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString;
      }

      // Format as relative time for recent dates
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

      if (diffInHours < 1) {
        const diffInMinutes = Math.floor(diffInHours * 60);
        return diffInMinutes <= 0 ? 'Just now' : `${diffInMinutes}m ago`;
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)}h ago`;
      } else if (diffInHours < 24 * 7) {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
      } else {
        // For older dates, show full date
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch {
      return dateString;
    }
  };

  const renderEventContent = (event: SessionEvent) => {
    const isUser = event.author === 'user';
    const isBot = event.author === 'model' || event.author === 'assistant';
    const isSystem = event.author === 'system';

    // Extract text content
    let textContent = '';
    let hasImages = false;
    let hasFunctionCalls = false;

    if (event.content?.parts) {
      for (const part of event.content.parts) {
        if (part.text) {
          // Skip user_language annotations
          if (!part.text.startsWith('user_language:')) {
            textContent += part.text;
          }
        }
        if (part.function_call) {
          hasFunctionCalls = true;
          if (!part.function_call.name?.includes('internal')) {
            textContent += `\n🔧 Using: ${part.function_call.name}`;
          }
        }
        if (part.function_response) {
          if (!part.function_response.name?.includes('internal')) {
            textContent += `\n✅ Analyzed: ${part.function_response.name}`;
          }
        }
      }
    }

    // Skip events with no meaningful content or system events without text
    if (!textContent.trim() && !event.actions?.state_delta) {
      return null;
    }

    // Skip internal system events that are not user-facing
    if (isSystem && !textContent.trim()) {
      return null;
    }

    return (
      <div className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div className={`flex gap-3 max-w-4xl ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Avatar */}
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isUser ? 'bg-blue-500' : isBot ? 'bg-gray-600' : 'bg-orange-500'
            }`}>
            {isUser ? (
              <User className="w-4 h-4 text-white" />
            ) : isBot ? (
              <Bot className="w-4 h-4 text-white" />
            ) : (
              <Database className="w-4 h-4 text-white" />
            )}
          </div>

          {/* Message Content */}
          <div className={`rounded-lg px-4 py-2 max-w-2xl ${isUser
            ? 'bg-blue-500 text-white'
            : isBot
              ? 'bg-gray-100 text-gray-900 border border-gray-200'
              : 'bg-orange-100 text-orange-900 border border-orange-200'
            }`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium opacity-75 capitalize">
                {event.author}
              </span>
              <span className="text-xs opacity-60">
                {formatDate(event.timestamp)}
              </span>
              {hasFunctionCalls && (
                <span className="text-xs bg-white bg-opacity-20 px-1 rounded">
                  Tool Use
                </span>
              )}
            </div>

            {textContent && (
              <div className="text-sm whitespace-pre-wrap">
                {textContent}
              </div>
            )}

            {event.actions?.state_delta && (
              <div className="mt-2 text-xs opacity-90">
                <details>
                  <summary className="cursor-pointer font-medium mb-1 select-none">System State Update</summary>
                  <div className={`mt-1 rounded overflow-hidden border ${isUser ? 'border-blue-300 bg-blue-900 bg-opacity-20' : 'border-gray-200 bg-white'}`}>
                    <JsonTreeViewer data={event.actions.state_delta} maxHeightClass="max-h-48" />
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AppLayout pageTitle="Session Details">
        <MobileHeader title="Session Details" showBack onBackClick={() => navigate(-1)} />
        <div className="flex-1 overflow-auto bg-main-bg min-h-full">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading session details...</p>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout pageTitle="Session Details">
        <MobileHeader title="Session Details" showBack onBackClick={() => navigate(-1)} />
        <div className="flex-1 overflow-auto bg-main-bg min-h-full">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="mb-6">
              <Link
                to="/admin"
                className="inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Panel
              </Link>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
                <div>
                  <h3 className="text-red-800 font-medium">Error Loading Session</h3>
                  <p className="text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!sessionData) {
    return null;
  }

  return (
    <AppLayout pageTitle="Session Details">
      <MobileHeader title="Session Details" showBack onBackClick={() => navigate(-1)} />
    <div className="flex-1 overflow-auto bg-main-bg min-h-full">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back navigation */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Link
              to="/admin"
              className="inline-flex items-center text-blue-600 hover:text-blue-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin Panel
            </Link>
            <button
              onClick={() => navigate('/admin/users')}
              className="inline-flex items-center text-gray-600 hover:text-gray-800"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              View All Sessions
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {sessionData.user.picture ? (
                  <img
                    src={sessionData.user.picture}
                    alt={sessionData.user.name}
                    className="w-12 h-12 rounded-full"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {sessionData.user.name}'s Chat Session
                  </p>
                  <p className="text-gray-600">{sessionData.user.email}</p>
                </div>
              </div>
              <div className="text-right text-sm text-gray-500">
                <p className="flex items-center">
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Session ID: {sessionData.session.id}
                </p>
                <p className="flex items-center mt-1">
                  <Calendar className="w-4 h-4 mr-1" />
                  Created: {formatDate(sessionData.session.created_at)}
                </p>
                <p className="flex items-center mt-1">
                  <Clock className="w-4 h-4 mr-1" />
                  Updated: {formatDate(sessionData.session.updated_at)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Conversation Statistics */}
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Conversation Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <User className="w-6 h-6 text-blue-600 mr-2" />
                <div>
                  <p className="text-sm text-blue-600">User Messages</p>
                  <p className="text-xl font-bold text-blue-900">
                    {sessionData.events ? sessionData.events.filter(e => e.author === 'user').length : 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <Bot className="w-6 h-6 text-gray-600 mr-2" />
                <div>
                  <p className="text-sm text-gray-600">Bot Responses</p>
                  <p className="text-xl font-bold text-gray-900">
                    {sessionData.events ? sessionData.events.filter(e => e.author === 'model' || e.author === 'assistant').length : 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <Database className="w-6 h-6 text-green-600 mr-2" />
                <div>
                  <p className="text-sm text-green-600">System Events</p>
                  <p className="text-xl font-bold text-green-900">
                    {sessionData.events ? sessionData.events.filter(e => e.author === 'system').length : 0}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center">
                <MessageSquare className="w-6 h-6 text-purple-600 mr-2" />
                <div>
                  <p className="text-sm text-purple-600">Total Events</p>
                  <p className="text-xl font-bold text-purple-900">
                    {sessionData.events ? sessionData.events.length : 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Part Decisions (Inspector) */}
        <PartDecisionsPanel state={sessionData.session.state} />

        {/* Session State (if any) */}
        {sessionData.session.state && Object.keys(sessionData.session.state).length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Session State</h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {Object.keys(sessionData.session.state).length} keys
              </span>
            </div>
            <JsonTreeViewer data={sessionData.session.state} maxHeightClass="max-h-96" />
          </div>
        )}

        {/* Chat Messages */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Chat Messages</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {sessionData.events ? sessionData.events.length : 0} event(s) in this conversation
                </p>
              </div>
              <div className="flex items-center gap-4">
                <select
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value as any)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1"
                >
                  <option value="conversation">Conversation Only</option>
                  <option value="all">All Events</option>
                  <option value="system">System Events Only</option>
                </select>
              </div>
            </div>
          </div>

          <div className="p-6 min-h-96">
            {sessionData.events && sessionData.events.length > 0 ? (
              <div className="space-y-4">
                {sessionData.events
                  .filter(event => {
                    if (eventFilter === 'conversation') {
                      return event.author === 'user' || event.author === 'model' || event.author === 'assistant';
                    } else if (eventFilter === 'system') {
                      return event.author === 'system';
                    }
                    return true; // 'all'
                  })
                  .map((event, index) => {
                    const renderedEvent = renderEventContent(event);
                    return renderedEvent ? (
                      <div key={event.id || index}>
                        {renderedEvent}
                      </div>
                    ) : null;
                  })
                  .filter(Boolean)}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No messages in this session</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </AppLayout>
  );
};

export default AdminSessionDetailsPage;
